import styled from 'styled-components';
import { Trans } from 'react-i18next';
import { useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';

import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { ITabName } from 'checkout/types/Tab';
import { Button } from 'shared/components/Button';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useCurrency } from 'shared/hooks/useCurrency';
import { ReviewPriceBreakdown } from 'checkout/components/GlobalPricingBreakdown/ReviewPriceBreakdown';
import CvvVerification from 'checkout/components/Payment/CvvVerification';
import { logEcomPurchase } from 'tracking/utils/eCommerceEvents';
import { PaymentType } from 'shared/enums/PaymentType';
import { PriceBreakdownType } from 'shared/enums/PriceBreakdown';
import { FeatureFlags } from 'featureFlags/constants';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { ChangeCurrencyModal } from 'checkout/components/Payment/ChangeCurrencyModal';
import { PaymentTypeToPaymentVendor } from 'checkout/enums/PaymentVendor';
import { variableFont, variableFontRegular } from 'styles/theme';
import { USD_ONLY_PAYMENT_VENDORS } from 'checkout/enums/UsdOnlyPaymentVendors';
import { fireTiktokCheckoutPlaceOrder } from 'tracking/utils/tiktokEvents';
import { TabPanelWrapper } from 'checkout/components/Tabs/Components';
import { GiftCardEventTypes } from 'gift-cards/enums/constants';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { startCase } from 'shared/utils/startCase';

const TP = 'checkout.components.Tabs.ReviewTab';

export const ReviewTab = () => {
  const {
    addresses = {},
    billingInfos = {},
    changeTab,
    order,
    orderReadyForPurchase,
    orderSubmitting,
    purchaseOrder,
    want,
  } = useCheckoutContext();
  const { user } = useStoreSelector((state) => state.auth);
  const { selectedCurrencyCode } = useCurrency();

  const isTiktokEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_TIKTOK);
  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);
  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );
  const isOfferSettledInUsd = useFeatureFlag(FeatureFlags.TEMP_SETTLE_OFFERS_IN_USD);

  const [showChangeCurrencyModal, setShowChangeCurrencyModal] = useState(false);
  const [purchaseButtonDisabled, setPurchaseButtonDisabled] = useState(true);
  const [purchaseButtonLoading, setPurchaseButtonLoading] = useState(true);

  const shippingAddress = addresses?.[order?.addressId];
  const billingInfo = billingInfos?.[order?.billingInfoId];
  const billingAddress = addresses?.[billingInfo?.billingAddressId];

  const { forAuction, isOfferable } =
    order?.product?.productTemplate || want?.productTemplate || {};
  const isAuction = !!order?.wantId && forAuction && isCheckoutOffersEnabled;
  const isOffer = !!order?.wantId && !forAuction && isOfferable;
  const isPurchasingGiftCard = !!order?.product?.giftCardId && isCheckoutPurchaseGiftCardsEnabled;

  let giftCardType = GiftCardEventTypes.NULL;
  if (order?.product?.giftCardId) {
    giftCardType = GiftCardEventTypes.PURCHASED;
  }
  if (order?.giftCards?.length > 0) {
    giftCardType = GiftCardEventTypes.APPLIED;
  }

  const placeOrderButtonTitle = (() => {
    if (isOffer) return t(`${TP}.placeOffer`, 'Place Offer');
    if (isAuction) return t(`${TP}.securelyPlaceOffer`, 'Securely Place Offer');
    if (order.verificationNeeded) return t(`${TP}.verifyCVV`, 'Verify CVV to Continue');
    return t(`${TP}.securelyPlaceOrder`, 'Securely Place Order');
  })();

  // CHECKOUT TODO: do not think this will ever be equal as we create a new billing address
  // with shipping address information
  const billingSameAsShipping = billingAddress?.id === shippingAddress?.id;

  const selectedShippingServiceLevelOption = order?.shippingServiceLevelOptions.find(
    (option) => option.id === order?.shippingServiceLevel,
  );

  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());

  const isSuccessfulAffirmRedirect = params.checkout_token && params.status !== 'CANCELLED';
  const isSuccessfulAfterpayRedirect = params.orderToken && params.status !== 'CANCELLED';

  const handlePurchaseOrderClick = async () => {
    if (
      selectedCurrencyCode !== 'USD' &&
      USD_ONLY_PAYMENT_VENDORS.includes(PaymentTypeToPaymentVendor[billingInfo?.paymentType])
    ) {
      setShowChangeCurrencyModal(true);
      return;
    }

    try {
      let paymentType: PaymentType;

      switch (billingInfo?.paymentType) {
        default:
          paymentType = PaymentType.CreditCard;
          break;
      }

      await purchaseOrder({ paymentType });

      // BUY3-1084 - analytics team is not sure why there are two of these events that do similar things
      // one is a custom event, the other is an mParticle built-in event that funnels data to other systems
      logEcomPurchase(
        {
          name: order?.product?.productTemplate?.name,
          sku: order?.product?.productTemplate?.sku,
          priceCents: order?.product?.localizedPriceCents?.amountUsdCents,
          brandName: order?.product?.productTemplate?.brandName,
          condition: order?.product?.shoeCondition,
        },
        order,
      );

      sendTrackingEvent('CART_PURCHASE', {
        content_type: 'product_group',
        currency_code: selectedCurrencyCode,
        goat_storage: false,
        instant_ship: order?.isInstantShip,
        is_apparel_product: order?.product?.productTemplate?.isFashionProduct,
        is_guest: user?.isGuest,
        order_id: order?.id,
        order_number: order?.number,
        price: order?.localizedPriceCents?.amount,
        product_template_slug: order?.product?.productTemplate?.slug,
        shipping_method: order?.shippingServiceLevel,
        shipping_price: order?.shippingCents,
        size: order?.product?.size,
        subtotal_price_dollars: ((order?.localizedPriceCents?.amountUsdCents || 0) / 100).toFixed(
          2,
        ),
        subtotal_price: order?.localizedPriceCents?.amount,
        gift_card_type: giftCardType,
      });

      if (isOffer) {
        sendTrackingEvent('CHECKOUT_PLACE_OFFER_TAP', {
          product_template_slug: want?.productTemplate?.slug,
          size: want?.sizeOption?.presentation,
          product_id: want?.productTemplate?.id,
          condition_category: want?.shoeCondition,
          shoe_conditon: want?.shoeCondition,
          box_condition: want?.boxCondition,
          want_id: order?.wantId,
          offer_duration: want?.offerDuration,
          offer_amount: want?.offerAmountCents / 100,
          currency_code: selectedCurrencyCode,
          order_number: order?.number,
        });
      }

      if (isTiktokEnabled) {
        fireTiktokCheckoutPlaceOrder(
          {
            contentCategory: order?.product?.productTemplate?.brandName,
            contentId: order?.product?.productTemplate?.sku,
            currency: selectedCurrencyCode,
            price: order?.localizedPriceCents?.amount,
            quantity: 1,
            value: order?.localizedFinalPriceCents?.amount,
          },
          user?.email,
        );
      }
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: order?.number });
      });
    }
  };

  useEffect(() => {
    sendTrackingEvent('CHECKOUT_VIEW', {
      box_condition: order?.product?.boxCondition,
      condition_category: order?.product?.shoeCondition,
      currency_code: selectedCurrencyCode,
      instant_ship: order?.isInstantShip,
      order_number: order?.number,
      price: order?.localizedPriceCents?.amount,
      product_template_slug: order?.product?.productTemplate?.slug,
      shoe_condition: order?.product?.shoeCondition,
      size: order?.product?.size,
      step: 'review',
      gift_card_type: giftCardType,
    });
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPurchaseButtonDisabled(orderSubmitting || !orderReadyForPurchase);
    setPurchaseButtonLoading(orderSubmitting);

    /**
     * NOTE: With auto purchase FFs enabled for affirm and afterpay there is a brief
     * moment that the user can click on the 'place order' button which will cause
     * an extra purchase requests to fire. Therefore for auto purchases will disable
     * and set purchase button to loading when we have a successful redirect as opposed
     * to the previous flow where with a success redirect the user must click on the
     * 'place order' button to complete
     */
    setPurchaseButtonDisabled((prev) => prev || isSuccessfulAffirmRedirect);
    setPurchaseButtonLoading((prev) => prev || isSuccessfulAffirmRedirect);

    setPurchaseButtonDisabled((prev) => prev || isSuccessfulAfterpayRedirect);
    setPurchaseButtonLoading((prev) => prev || isSuccessfulAfterpayRedirect);
  }, [
    order?.paymentType,
    orderSubmitting,
    orderReadyForPurchase,
    isSuccessfulAffirmRedirect,
    isSuccessfulAfterpayRedirect,
  ]);

  return (
    <TabPanelWrapper data-qa="review_order_panel">
      {((user?.isGuest && isGuestCheckoutEnabled) || isPurchasingGiftCard) && (
        <>
          <ReviewTab.SectionTitle>{t(`${TP}.guestEmail`, 'Email')}</ReviewTab.SectionTitle>
          <ReviewTab.ThreeCellContainer>
            <ReviewTab.Text data-qa="checkout_review_guest_email">{user?.email}</ReviewTab.Text>
          </ReviewTab.ThreeCellContainer>
        </>
      )}
      {!isPurchasingGiftCard && (
        <>
          <ReviewTab.SectionTitle>{t(`${TP}.shipping`, 'Shipping')}</ReviewTab.SectionTitle>
          <ReviewTab.ThreeCellContainer>
            <ReviewTab.ThreeCellOne>
              <ReviewTab.Text data-qa="checkout_review_shipping_name">
                {shippingAddress?.name}
              </ReviewTab.Text>
              <ReviewTab.Text data-qa="checkout_review_shipping_address1">
                {shippingAddress?.address1}
              </ReviewTab.Text>
              {shippingAddress?.address2 && (
                <ReviewTab.Text data-qa="checkout_review_shipping_address2">
                  {shippingAddress?.address2}
                </ReviewTab.Text>
              )}
              <ReviewTab.Text>
                <span data-qa="checkout_shipping_address_city">{shippingAddress?.city}</span>,{' '}
                <span data-qa="checkout_shipping_address_state">{shippingAddress?.state}</span>{' '}
                <span data-qa="checkout_shipping_address_postalCode">
                  {shippingAddress?.postalCode}
                </span>
              </ReviewTab.Text>
            </ReviewTab.ThreeCellOne>
            <ReviewTab.ThreeCellTwo>
              <ReviewTab.Text>
                <span data-qa="checkout_shipping_service_level">
                  {startCase(order?.shippingServiceLevel)}
                </span>
                :{' '}
                <span data-qa="checkout_shipping_service_level_price">
                  {formatMoneyObject(order?.localizedShippingCents, {
                    showInUsd: isOffer && isOfferSettledInUsd,
                  })}
                </span>
              </ReviewTab.Text>
              {selectedShippingServiceLevelOption && (
                <ReviewTab.Text>{selectedShippingServiceLevelOption?.estimation}</ReviewTab.Text>
              )}
            </ReviewTab.ThreeCellTwo>
            <ReviewTab.ThreeCellThree>
              <ReviewTab.EditButton onClick={() => changeTab(ITabName.SHIPPING)}>
                {t(`${TP}.edit`, 'Edit')}
              </ReviewTab.EditButton>
            </ReviewTab.ThreeCellThree>
          </ReviewTab.ThreeCellContainer>
        </>
      )}

      <ReviewTab.SectionTitle>{t(`${TP}.payment`, 'Payment')}</ReviewTab.SectionTitle>
      <ReviewTab.ThreeCellContainer>
        {billingInfo?.paymentType === PaymentType.CreditCard ? (
          <>
            <ReviewTab.ThreeCellOne>
              {billingSameAsShipping ? (
                t(`${TP}.paymentSame`, 'Billing address same as shipping')
              ) : (
                <>
                  <ReviewTab.Text data-qa="checkout_review_billing_address_name">
                    {billingAddress?.name}
                  </ReviewTab.Text>
                  <ReviewTab.Text data-qa="checkout_review_billing_address_address1">
                    {billingAddress?.address1}
                  </ReviewTab.Text>
                  {billingAddress?.address2 && (
                    <ReviewTab.Text data-qa="checkout_review_billing_address_address2">
                      {billingAddress?.address2}
                    </ReviewTab.Text>
                  )}
                  <ReviewTab.Text>
                    <span data-qa="checkout_billing_address_city">{billingAddress?.city}</span>,{' '}
                    <span data-qa="checkout_billing_address_state">{billingAddress?.state}</span>{' '}
                    <span data-qa="checkout_billing_address_postalCode">
                      {billingAddress?.postalCode}
                    </span>
                  </ReviewTab.Text>
                </>
              )}
            </ReviewTab.ThreeCellOne>
            <ReviewTab.ThreeCellTwo data-qa="checkout_review_card_preview">
              {billingInfo?.cardBrand} *{billingInfo?.last4Digits}
            </ReviewTab.ThreeCellTwo>
          </>
        ) : (
          <>
            <ReviewTab.ThreeCellOne data-qa="checkout_review_billing_email">
              {billingInfo?.email}
            </ReviewTab.ThreeCellOne>
            {billingInfo?.paymentType === PaymentType.Affirm && (
              <ReviewTab.ThreeCellTwo>
                <ReviewTab.PaymentMethodLogo
                  data-qa="checkout_affirm_logo"
                  src="/images/icons/affirm.png"
                  alt="affirm logo"
                />
              </ReviewTab.ThreeCellTwo>
            )}
            {billingInfo?.paymentType === PaymentType.AfterPay && (
              <ReviewTab.ThreeCellTwo>
                <ReviewTab.PaymentMethodLogo
                  data-qa="checkout_afterpay_logo"
                  src="/images/icons/afterpay.svg"
                  alt="afterpay logo"
                />
              </ReviewTab.ThreeCellTwo>
            )}
          </>
        )}
        <ReviewTab.ThreeCellThree>
          <ReviewTab.EditButton
            onClick={() => changeTab(ITabName.PAYMENT)}
            data-qa="checkout_review_payment_edit_button"
          >
            {t(`${TP}.edit`, 'Edit')}
          </ReviewTab.EditButton>
        </ReviewTab.ThreeCellThree>
      </ReviewTab.ThreeCellContainer>
      <CvvVerification />
      <ReviewTab.SectionTitle>
        {isOffer || isAuction
          ? t(`${TP}.offerSummary`, 'Offer Summary')
          : t(`${TP}.orderSummary`, 'Order Summary')}
      </ReviewTab.SectionTitle>

      {isPurchasingGiftCard ? (
        <ReviewTab.GiftCardPriceBreakdown>
          <ReviewTab.GiftCardPriceBreakdownText>
            {t(`${TP}.giftCardSummaryOrderTotal`, 'Total')}
          </ReviewTab.GiftCardPriceBreakdownText>
          <ReviewTab.GiftCardPriceBreakdownText>
            {/* As of now giftcards are always in USD */}
            {formatMoneyObject({
              currency: 'USD',
              amount: order?.localizedFinalPriceCents?.amountUsdCents,
            })}
          </ReviewTab.GiftCardPriceBreakdownText>
        </ReviewTab.GiftCardPriceBreakdown>
      ) : (
        <ReviewPriceBreakdown />
      )}
      <ReviewTab.LegalDisclaimer data-qa="checkout_legal_disclaimer">
        {order?.priceBreakdownType === PriceBreakdownType.DEFAULT &&
          order?.internationalCheckoutNote && (
            <p data-qa="checkout_international_checkout_note">{order?.internationalCheckoutNote}</p>
          )}
        {order?.finalSale && (
          <span data-qa="checkout_review_final_sale_disclaimer">
            {t(`${TP}.final_sale_warning`, 'This item is Final Sale.')}{' '}
          </span>
        )}
        {isOffer && (
          <ReviewTab.LegalDisclaimerWrapper>
            <Trans
              i18nKey={`${TP}.review_legal_disclaimer_offer`}
              defaults="By proceeding, you confirm that you have read and agree to the <termsOfServiceLink>Terms of Service</termsOfServiceLink> and <purchasePolicyLink>Purchases &amp; Returns Policy.</purchasePolicyLink>"
              components={{
                termsOfServiceLink: (
                  <ReviewTab.Link
                    href={getRedirectionPath('/terms')}
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
                purchasePolicyLink: (
                  <ReviewTab.Link
                    href={getRedirectionPath('/returns')}
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </ReviewTab.LegalDisclaimerWrapper>
        )}
        {billingInfo?.paymentType !== PaymentType.AfterPay && !isOffer && !isPurchasingGiftCard && (
          <Trans
            i18nKey={`${TP}.review_legal_disclaimer`}
            defaults="By proceeding, you confirm you have read and agree to the <purchasePolicyLink>Purchases &amp; Returns Policy</purchasePolicyLink>."
            components={{
              purchasePolicyLink: (
                <ReviewTab.Link
                  href={getRedirectionPath('/returns')}
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        )}
        {billingInfo?.paymentType === PaymentType.AfterPay && !isOffer && !isPurchasingGiftCard && (
          <Trans
            i18nKey={`${TP}.review_legal_disclaimer_afterpay`}
            defaults="By proceeding, you confirm you have read and agree to the <purchasePolicyLink>Purchases &amp; Returns Policy</purchasePolicyLink> and Afterpay's <afterpayAgreementLink>Installment Agreement</afterpayAgreementLink>."
            components={{
              purchasePolicyLink: (
                <ReviewTab.Link
                  href={getRedirectionPath('/returns')}
                  target="_blank"
                  rel="noreferrer"
                />
              ),
              afterpayAgreementLink: (
                <ReviewTab.Link
                  href="https://www.afterpay.com/purchase-payment-agreement"
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        )}
        {isPurchasingGiftCard && (
          <Trans
            i18nKey={`${TP}.legalDisclaimerPurchasingGiftCard`}
            defaults="By proceeding, you confirm you have read and agree to the <br> <giftCardTermsLink>Gift Card Terms</giftCardTermsLink>."
            components={{
              giftCardTermsLink: (
                <ReviewTab.Link
                  href={getRedirectionPath('/gift-card-terms')}
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        )}
      </ReviewTab.LegalDisclaimer>
      <ReviewTab.PlaceOrderButton
        data-qa="checkout_review_place_order"
        disabled={purchaseButtonDisabled}
        type="button"
        onClick={handlePurchaseOrderClick}
        loading={purchaseButtonLoading}
        title={placeOrderButtonTitle}
      />
      {isOffer && (
        <ReviewTab.OfferTagline data-qa="offer_tag_line">
          {selectedCurrencyCode === 'USD'
            ? t(`${TP}.wontBeChargedUnless`, 'You won’t be charged unless your offer is accepted.')
            : t(
                `${TP}.wontBeChargedUnlessUsd`,
                'Your accepted offer will transact in USD. You won’t be charged unless your offer is accepted.',
              )}
        </ReviewTab.OfferTagline>
      )}
      {showChangeCurrencyModal && (
        <ChangeCurrencyModal
          onClose={() => setShowChangeCurrencyModal(false)}
          selectedPaymentVendor={PaymentTypeToPaymentVendor[billingInfo?.paymentType]}
        />
      )}
    </TabPanelWrapper>
  );
};

ReviewTab.ThreeCellContainer = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 40px;
`;

ReviewTab.ThreeCellOne = styled.div`
  flex: 5 3;
`;

ReviewTab.ThreeCellTwo = styled.div`
  flex: 4 4;

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    flex: 2 2;
  }
`;

ReviewTab.ThreeCellThree = styled.div`
  flex: 1 1 50px;
  text-align: right;

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    flex: 1 1 25px;
  }
`;

ReviewTab.EditButton = styled.button`
  text-decoration: underline;
  padding-top: 0;
  color: ${({ theme }) => theme.colors.black};
`;

ReviewTab.LegalDisclaimer = styled.div`
  color: ${({ theme }) => theme.colors.black};
  margin: 43px 0 47px;
  ${variableFontRegular({ size: '11px', fontWeight: '400' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ size: '11.5px' })};
  }

  p {
    margin-bottom: 16px;
  }
`;

ReviewTab.LegalDisclaimerWrapper = styled.div`
  // Mobile
  width: 100%;

  // Desktop
  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    width: 315px;
  }
`;

ReviewTab.Link = styled.a`
  text-decoration: underline;
  white-space: nowrap;
`;

ReviewTab.PlaceOrderButton = styled(Button)`
  font-size: 11px;
  width: 100%;
  text-transform: uppercase;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    height: 42px;
    font-size: 11.5px;
  }
`;

ReviewTab.Text = styled.p`
  margin-bottom: 4px;
  font-size: 11px;
  ${variableFont({ size: '11px', fontWeight: '400' })};
  line-height: 14px;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFont({ size: '11.5px', fontWeight: '400' })};
  }
`;

ReviewTab.PaymentMethodLogo = styled.img`
  position: relative;
  top: -2px;
  height: 20px;
  padding: 0 11px;
`;

ReviewTab.SectionTitle = styled.h5`
  margin: 0;
  padding: 0 0 16px 0;
  ${variableFont({ size: '12px', fontWeight: '700' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFont({ size: '12.5px', fontWeight: '700' })};
  }
`;

ReviewTab.GiftCardPriceBreakdown = styled.div`
  display: flex;
  justify-content: space-between;
`;

ReviewTab.GiftCardPriceBreakdownText = styled.p`
  ${variableFontRegular({ size: '11.5px' })};
  margin: 0;
`;

ReviewTab.OfferTagline = styled.p`
  margin-top: 16px;
  font-size: 9.5px;
`;
